import { memo, useId } from 'react';
import type { ReactNode } from 'react';
import type { Era } from '../../art.js';

/**
 * The pictures MEND THE PICTURE cuts into tiles: bold, simple scenes in a 120 × 120 box — a ship
 * at sunset, a ringed planet, a city at night, a tree on a hill, a lighthouse, mountains, a house,
 * a balloon, a rocket. No people. Each is laid out so that every tile of a 3 × 3 cut holds
 * something with a clear way up (a horizon, a cloud, a roof), so a turned tile always looks wrong.
 */

export type SceneId = 'ship' | 'planet' | 'city' | 'tree' | 'lighthouse' | 'mountains' | 'house' | 'balloon' | 'rocket';

export const SCENE_IDS: readonly SceneId[] = ['ship', 'planet', 'city', 'tree', 'lighthouse', 'mountains', 'house', 'balloon', 'rocket'];

/** The pictures each era's archive holds: posters in the city, family photos in the garden. */
export const SCENES_BY_ERA: Record<Era, readonly SceneId[]> = {
  stars: ['planet', 'ship', 'mountains', 'balloon', 'lighthouse'],
  neon: ['city', 'rocket', 'planet', 'balloon'],
  debris: ['ship', 'lighthouse', 'rocket', 'planet'],
  dust: ['mountains', 'balloon', 'lighthouse', 'tree'],
  canopy: ['house', 'tree', 'lighthouse', 'mountains', 'ship'],
};

const Sky = ({ id, stops }: { id: string; stops: readonly [number, string][] }) => (
  <>
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
        {stops.map(([o, c]) => (
          <stop key={o} offset={o} stopColor={c} />
        ))}
      </linearGradient>
    </defs>
    <rect x="0" y="0" width="120" height="120" fill={`url(#${id})`} />
  </>
);

function Cloud({ x, y, s = 1, fill = '#ffffff', o = 0.92 }: { x: number; y: number; s?: number; fill?: string; o?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`} opacity={o}>
      <path d="M-15 5C-17 -1 -11 -5 -7 -3C-5 -10 5 -11 8 -4C12 -7 17 -2 15 5Z" fill={fill} />
      <path d="M-13 4H13" stroke="#000000" strokeOpacity=".08" strokeWidth="1.2" />
    </g>
  );
}

function Birds({ x, y, s = 1, c = '#1b2433' }: { x: number; y: number; s?: number; c?: string }) {
  return (
    <path
      transform={`translate(${x} ${y}) scale(${s})`}
      d="M-6 0q3 -3.4 6 0q3 -3.4 6 0M5 6q2.4 -2.8 5 0q2.4 -2.8 5 0"
      fill="none"
      stroke={c}
      strokeWidth="1.3"
      strokeLinecap="round"
    />
  );
}

function Sparkle({ x, y, r, c = '#ffffff' }: { x: number; y: number; r: number; c?: string }) {
  const q = r * 0.22;
  return <path d={`M${x} ${y - r}L${x + q} ${y - q}L${x + r} ${y}L${x + q} ${y + q}L${x} ${y + r}L${x - q} ${y + q}L${x - r} ${y}L${x - q} ${y - q}Z`} fill={c} />;
}

const DOTS: readonly [number, number][] = [
  [8, 8], [34, 6], [47, 22], [70, 6], [112, 30], [6, 36], [30, 44], [92, 8], [104, 52], [18, 70], [62, 30], [116, 66],
];

function Ship({ p }: { p: string }) {
  return (
    <>
      <Sky id={`${p}s`} stops={[[0, '#16244f'], [0.4, '#6a3f7a'], [0.6, '#f08a5d']]} />
      <circle cx="86" cy="72" r="24" fill="#ffb36b" opacity=".22" />
      <circle cx="86" cy="72" r="14" fill="#ffd98a" />
      <Cloud x={20} y={18} s={1.4} fill="#f7c6b4" />
      <Cloud x={64} y={30} s={1.05} fill="#e9a7a6" />
      <Cloud x={104} y={12} s={0.9} fill="#f7c6b4" />
      <Birds x={96} y={40} />
      <rect x="0" y="72" width="120" height="48" fill="#1c3a6a" />
      <rect x="0" y="92" width="120" height="28" fill="#10244a" />
      <path d="M72 76h28M76 81h20M79 86h14M82 91h8" stroke="#ffd98a" strokeWidth="1.8" opacity=".85" strokeLinecap="round" />
      <path d="M4 98q5-3 10 0t10 0M36 108q5-3 10 0t10 0M84 104q5-3 10 0t10 0M8 114q5-3 10 0t10 0M64 115q5-3 10 0t10 0M100 114q4-3 8 0" stroke="#7fb0e0" strokeWidth="1.4" fill="none" opacity=".75" />
      {/* the ship */}
      <path d="M26 74V36M40 74V26M53 74V40M62 74L74 64" stroke="#3a2210" strokeWidth="1.8" />
      <path d="M40 26L49 28.5L40 31Z" fill="#d64541" />
      <g fill="#f4ead2" stroke="#8a7a5a" strokeWidth=".8">
        <path d="M18 40Q26 38 34 40L33 52Q26 50 19 52Z" />
        <path d="M17 55Q26 53 35 55L34 68Q26 66 18 68Z" />
        <path d="M31 32Q40 30 49 32L48 45Q40 43 32 45Z" />
        <path d="M30 48Q40 46 50 48L49 64Q40 62 31 64Z" />
        <path d="M46 44Q53 42 60 44L59 55Q53 53 47 55Z" />
        <path d="M45 58Q53 56 61 58L60 69Q53 67 46 69Z" />
        <path d="M62 72L73 64L66 60Z" />
      </g>
      <path d="M10 72H66L58 88H19Z" fill="#6b3a1f" stroke="#2a1608" strokeWidth="1" />
      <path d="M13 77H63" stroke="#e8c080" strokeWidth="1.5" />
      <path d="M22 82h2M32 82h2M42 82h2M52 82h2" stroke="#2a1608" strokeWidth="2" />
    </>
  );
}

function Planet({ p }: { p: string }) {
  return (
    <>
      <Sky id={`${p}s`} stops={[[0, '#050918'], [0.7, '#1b2b66'], [1, '#2b3f86']]} />
      <defs>
        <radialGradient id={`${p}g`} cx=".35" cy=".3" r=".8">
          <stop offset="0" stopColor="#ffe0a8" />
          <stop offset=".55" stopColor="#d9773a" />
          <stop offset="1" stopColor="#5a2410" />
        </radialGradient>
      </defs>
      {DOTS.map(([x, y]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r=".9" fill="#dfe9ff" opacity=".8" />
      ))}
      <Sparkle x={14} y={52} r={4} />
      <Sparkle x={54} y={10} r={3} />
      <Sparkle x={50} y={68} r={2.6} c="#ffe0a8" />
      <Sparkle x={112} y={82} r={3} />
      {/* a crescent moon */}
      <path d="M22 13a11 11 0 1 0 10 16a9 9 0 1 1-10-16Z" fill="#e8e4d0" />
      {/* a comet */}
      <path d="M36 62L6 50L8 46Z" fill="#9fd2ee" opacity=".55" />
      <circle cx="36" cy="62" r="2.6" fill="#ffffff" />
      {/* the ringed planet */}
      <g transform="rotate(-18 82 42)">
        <ellipse cx="82" cy="42" rx="36" ry="8" fill="none" stroke="#f3dcb0" strokeWidth="3" opacity=".75" />
      </g>
      <circle cx="82" cy="42" r="20" fill={`url(#${p}g)`} />
      <path d="M64 36Q82 30 100 34M63 45Q82 40 101 44" stroke="#8a3a18" strokeWidth="2" fill="none" opacity=".45" />
      <g transform="rotate(-18 82 42)">
        <path d="M46 42A36 8 0 0 0 118 42" fill="none" stroke="#f3dcb0" strokeWidth="3" />
      </g>
      {/* the ground, with a small base */}
      <path d="M0 96Q20 88 40 94T80 92T120 90V120H0Z" fill="#3b3550" />
      <path d="M0 96Q20 88 40 94T80 92T120 90" fill="none" stroke="#6d6590" strokeWidth="1.5" />
      <ellipse cx="22" cy="106" rx="7" ry="2.6" fill="#28233a" />
      <ellipse cx="62" cy="110" rx="9" ry="3" fill="#28233a" />
      <ellipse cx="100" cy="112" rx="6" ry="2.2" fill="#28233a" />
      <path d="M86 92a10 8 0 0 1 20 0Z" fill="#cfd8e0" />
      <path d="M96 84V76" stroke="#cfd8e0" strokeWidth="1.4" />
      <circle cx="96" cy="75" r="1.8" fill="#ff8a3c" />
      <path d="M40 102L46 96L52 102" stroke="#6d6590" strokeWidth="1.2" fill="none" />
    </>
  );
}

const TOWERS: readonly [number, number, number][] = [
  [0, 16, 70], [15, 14, 56], [28, 12, 78], [38, 17, 62], [67, 14, 54], [80, 12, 72], [91, 16, 60], [106, 14, 78],
];

function City({ p }: { p: string }) {
  return (
    <>
      <Sky id={`${p}s`} stops={[[0, '#12052a'], [0.55, '#4a0f5e'], [1, '#ff4fd8']]} />
      {DOTS.slice(0, 8).map(([x, y]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r=".9" fill="#ffd1f4" opacity=".8" />
      ))}
      <circle cx="24" cy="24" r="13" fill="#ffe9f7" />
      <circle cx="20" cy="20" r="3" fill="#f2c6e4" />
      <circle cx="29" cy="28" r="2.2" fill="#f2c6e4" />
      <circle cx="27" cy="17" r="1.5" fill="#f2c6e4" />
      {/* an airship, nose to the left */}
      <path d="M78 22C78 16 88 13 98 13C106 13 112 17 114 22C112 27 106 31 98 31C88 31 78 28 78 22Z" fill="#4fe0ff" />
      <path d="M112 18L119 13V31L112 26Z" fill="#2aa8c8" />
      <path d="M92 31h10v4h-10Z" fill="#1a0b30" />
      <path d="M82 22H112" stroke="#ffffff" strokeWidth="1" opacity=".5" />
      {/* the spire */}
      <path d="M56 120V42L60 14L64 42V120Z" fill="#2a1244" />
      <path d="M60 14V4" stroke="#ff4fd8" strokeWidth="1.2" />
      <circle cx="60" cy="4" r="1.6" fill="#ff4fd8" />
      <path d="M57 50h6M57 60h6M57 70h6" stroke="#4fe0ff" strokeWidth="1.2" />
      {TOWERS.map(([x, w, top], b) => (
        <g key={x}>
          <rect x={x} y={top} width={w} height={120 - top} fill={b % 2 ? '#1a0b30' : '#23103d'} />
          <rect x={x} y={top} width={w} height="2" fill="#ff4fd8" opacity=".5" />
          {Array.from({ length: Math.floor((104 - top) / 7) }, (_, r) =>
            Array.from({ length: Math.floor((w - 2) / 5) }, (_, c) =>
              (r * 3 + c * 5 + b) % 3 === 0 ? (
                <rect key={`${r}-${c}`} x={x + 2.5 + c * 5} y={top + 6 + r * 7} width="2.4" height="3" fill={(r + b) % 2 ? '#ffd1f4' : '#4fe0ff'} opacity=".85" />
              ) : null,
            ),
          )}
        </g>
      ))}
      <rect x="0" y="106" width="120" height="14" fill="#0d0618" />
      <path d="M4 112h14M26 112h10M48 112h16M76 112h12M98 112h16" stroke="#ff4fd8" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M12 116h10M40 116h14M70 116h8M90 116h14" stroke="#4fe0ff" strokeWidth="1.4" strokeLinecap="round" />
    </>
  );
}

function Tree({ p }: { p: string }) {
  return (
    <>
      <Sky id={`${p}s`} stops={[[0, '#4f9ee8'], [1, '#d8f0ff']]} />
      <defs>
        <linearGradient id={`${p}h`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#7cc85a" />
          <stop offset="1" stopColor="#3f8a2e" />
        </linearGradient>
      </defs>
      <g stroke="#ffd84a" strokeWidth="1.6" strokeLinecap="round">
        <path d="M102 3v-4M102 33v4M87 18h-4M117 18h4M91 7l-3-3M113 29l3 3M113 7l3-3M91 29l-3 3" />
      </g>
      <circle cx="102" cy="18" r="10" fill="#ffe066" />
      <Cloud x={22} y={18} s={1.6} />
      <Cloud x={62} y={9} s={0.8} />
      <Birds x={16} y={48} c="#2b3a55" />
      <path d="M0 86Q40 66 80 80T120 78V120H0Z" fill={`url(#${p}h)`} />
      <path d="M58 86C61 72 61 62 59 50H69C67 62 67 72 72 86Z" fill="#7a4a22" />
      <path d="M68 58L84 50" stroke="#7a4a22" strokeWidth="3" strokeLinecap="round" />
      <g fill="#3f9a3a">
        <circle cx="50" cy="44" r="13" />
        <circle cx="64" cy="30" r="16" />
        <circle cx="80" cy="42" r="13" />
        <circle cx="64" cy="48" r="13" />
        <circle cx="46" cy="32" r="9" />
      </g>
      <g fill="#6cc85a">
        <circle cx="58" cy="24" r="7" />
        <circle cx="44" cy="38" r="5" />
        <circle cx="75" cy="36" r="5" />
      </g>
      <circle cx="54" cy="40" r="2.2" fill="#e8473c" />
      <circle cx="72" cy="28" r="2.2" fill="#e8473c" />
      {/* a swing on the branch */}
      <path d="M80 52V74M88 50V74" stroke="#5a3a1a" strokeWidth="1" />
      <path d="M78 74h12v2.4h-12Z" fill="#8a5a2a" />
      {/* a fence and flowers */}
      <g fill="#f3e6c8" stroke="#8a7a5a" strokeWidth=".6">
        <path d="M4 90l2-3 2 3v16h-4ZM13 90l2-3 2 3v16h-4ZM22 91l2-3 2 3v16h-4ZM31 92l2-3 2 3v14h-4Z" />
        <path d="M2 94H38V97H2ZM2 101H38V104H2Z" />
      </g>
      {[[92, 104, '#e8473c'], [100, 110, '#f2c230'], [108, 102, '#e060a8'], [114, 111, '#ffffff'], [86, 112, '#f2c230']].map(([x, y, c]) => (
        <g key={`${x}`}>
          <path d={`M${x} ${y}v7`} stroke="#2f6a26" strokeWidth="1" />
          <circle cx={x as number} cy={y as number} r="2.4" fill={c as string} />
        </g>
      ))}
      <path d="M44 120Q52 104 62 90" stroke="#d9c49a" strokeWidth="5" fill="none" opacity=".7" />
    </>
  );
}

function Lighthouse({ p }: { p: string }) {
  return (
    <>
      <Sky id={`${p}s`} stops={[[0, '#081030'], [1, '#2c4a7a']]} />
      {DOTS.map(([x, y]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r=".8" fill="#dfe9ff" opacity=".7" />
      ))}
      <path d="M18 8a10 10 0 1 0 9 14a8 8 0 1 1-9-14Z" fill="#f2ecd2" />
      <Sparkle x={58} y={70} r={2.6} c="#dfe9ff" />
      {/* the beam */}
      <path d="M96 33L0 12V54Z" fill="#fff3b0" opacity=".22" />
      <path d="M96 33L0 22V44Z" fill="#fff3b0" opacity=".18" />
      <rect x="0" y="86" width="120" height="34" fill="#0f2a4a" />
      <path d="M40 90h20M30 96h14M52 100h10" stroke="#fff3b0" strokeWidth="1.4" opacity=".45" strokeLinecap="round" />
      <path d="M6 106q5-3 10 0t10 0M40 112q5-3 10 0t10 0M60 100q4-3 8 0M16 116q4-3 8 0" stroke="#5f8fc0" strokeWidth="1.3" fill="none" />
      {/* the rocks and the tower */}
      <path d="M68 120V98Q74 90 84 92Q94 84 108 90Q116 92 120 96V120Z" fill="#2b2f38" />
      <path d="M72 104Q80 98 88 102M96 96Q104 94 112 100" stroke="#474e5c" strokeWidth="1.4" fill="none" />
      <path d="M87 94L90 40H102L105 94Z" fill="#f2f2f2" />
      <path d="M88.6 62L89.5 52H102.5L103.4 62ZM87.6 82L88.3 72H103.7L104.4 82Z" fill="#d64541" />
      <path d="M94 94V86a2 2 0 0 1 4 0V94Z" fill="#3a2a22" />
      <path d="M86 40H106V36H86Z" fill="#2b2f38" />
      <path d="M89 36V27H103V36Z" fill="#ffe8a0" />
      <path d="M96 27V36" stroke="#b58a3a" strokeWidth="1" />
      <path d="M87 27L96 19L105 27Z" fill="#d64541" />
      {/* a small boat */}
      <path d="M12 96H40L36 103H17Z" fill="#8a5a2a" />
      <path d="M26 96V74L38 94Z" fill="#f4ead2" />
      <path d="M25 96V76L16 94Z" fill="#e9dcc0" />
    </>
  );
}

function Mountains({ p }: { p: string }) {
  return (
    <>
      <Sky id={`${p}s`} stops={[[0, '#2f4f94'], [0.62, '#ffb88a']]} />
      <Cloud x={16} y={12} s={1} fill="#ffe6d8" />
      <Cloud x={104} y={14} s={1.1} fill="#ffe6d8" />
      <Birds x={64} y={16} c="#2b3050" />
      <circle cx="56" cy="62" r="11" fill="#ffe08a" />
      <path d="M-4 76L28 20L62 76Z" fill="#4a5a86" />
      <path d="M28 20L19 36L24 33L28 38L32 32L37 35Z" fill="#f4f7fb" />
      <path d="M28 20L62 76H44Z" fill="#000000" opacity=".15" />
      <path d="M48 76L90 28L126 76Z" fill="#3c4a74" />
      <path d="M90 28L80 41L86 38L90 43L94 38L100 41Z" fill="#f4f7fb" />
      <path d="M90 28L126 76H106Z" fill="#000000" opacity=".15" />
      {/* the lake, with the peaks upside down in it */}
      <rect x="0" y="76" width="120" height="26" fill="#6a86b8" />
      <path d="M-4 76L28 108L62 76Z" fill="#4a5a86" opacity=".4" />
      <path d="M48 76L90 104L126 76Z" fill="#3c4a74" opacity=".4" />
      <path d="M50 80h12M52 85h8M54 90h4" stroke="#ffe08a" strokeWidth="1.4" strokeLinecap="round" opacity=".8" />
      <path d="M0 102Q30 96 60 102T120 100V120H0Z" fill="#2f5d34" />
      {[[10, 100, 1], [21, 105, 0.8], [104, 98, 1.1], [114, 104, 0.8], [60, 108, 0.7]].map(([x, y, s]) => (
        <path
          key={`${x}`}
          transform={`translate(${x} ${y}) scale(${s})`}
          d="M0 -18L-6 -8H-3L-8 0H-2V4H2V0H8L3 -8H6Z"
          fill="#1f4526"
        />
      ))}
    </>
  );
}

function House({ p }: { p: string }) {
  return (
    <>
      <Sky id={`${p}s`} stops={[[0, '#5fa8e8'], [1, '#e6f4ff']]} />
      <circle cx="104" cy="16" r="9" fill="#ffe066" />
      <Cloud x={20} y={16} s={1.4} />
      <circle cx="80" cy="22" r="4" fill="#eef2f6" opacity=".9" />
      <circle cx="86" cy="13" r="5" fill="#eef2f6" opacity=".8" />
      <circle cx="94" cy="5" r="6" fill="#eef2f6" opacity=".7" />
      <path d="M0 88H120V120H0Z" fill="#5fae42" />
      <path d="M0 88H120" stroke="#8ad06a" strokeWidth="2" />
      <path d="M76 28H84V48L76 41Z" fill="#7a3a2a" />
      <path d="M34 58H90V98H34Z" fill="#f3e2c0" />
      <path d="M26 60L62 28L98 60Z" fill="#c8453c" />
      <path d="M26 60L62 28L98 60" fill="none" stroke="#7a2a22" strokeWidth="1.4" />
      <path d="M44 72H56V98H44Z" fill="#7a4a22" />
      <circle cx="53" cy="86" r="1.2" fill="#ffd84a" />
      <path d="M64 66H82V80H64Z" fill="#9fd2ee" stroke="#6a5a4a" strokeWidth="1" />
      <path d="M73 66V80M64 73H82" stroke="#f3e2c0" strokeWidth="1.2" />
      <path d="M56 44a6 6 0 0 1 12 0v6h-12Z" fill="#9fd2ee" stroke="#6a5a4a" strokeWidth="1" />
      <path d="M13 98V70H19V98Z" fill="#7a4a22" />
      <circle cx="16" cy="56" r="13" fill="#3f9a3a" />
      <circle cx="8" cy="66" r="8" fill="#3f9a3a" />
      <circle cx="25" cy="66" r="8" fill="#4fae44" />
      <path d="M46 98L38 120H62L54 98Z" fill="#d9c49a" />
      <g fill="#f3e6c8" stroke="#8a7a5a" strokeWidth=".6">
        <path d="M96 88l2-3 2 3v14h-4ZM105 88l2-3 2 3v14h-4ZM114 88l2-3 2 3v14h-4Z" />
        <path d="M92 91H120V94H92ZM92 97H120V100H92Z" />
      </g>
      {[[8, 108, '#e8473c'], [18, 112, '#f2c230'], [28, 106, '#e060a8'], [80, 110, '#f2c230'], [90, 114, '#e8473c'], [104, 110, '#ffffff']].map(([x, y, c]) => (
        <g key={`${x}`}>
          <path d={`M${x} ${y}v6`} stroke="#2f6a26" strokeWidth="1" />
          <circle cx={x as number} cy={y as number} r="2.4" fill={c as string} />
        </g>
      ))}
    </>
  );
}

function Balloon({ p }: { p: string }) {
  return (
    <>
      <Sky id={`${p}s`} stops={[[0, '#4f9ee8'], [1, '#d6ecff']]} />
      <Cloud x={18} y={22} s={1.6} />
      <Cloud x={100} y={18} s={1.2} />
      <Cloud x={26} y={70} s={1} o={0.8} />
      <Birds x={96} y={44} c="#2b3a55" />
      <path d="M54 10C70 10 79 22 79 35C79 50 64 58 60 66H48C44 58 29 50 29 35C29 22 38 10 54 10Z" fill="#e8473c" />
      <path d="M54 10C63 22 63 50 57 66H51C45 50 45 22 54 10Z" fill="#f2c230" />
      <path d="M54 10C40 18 36 40 44 60M54 10C68 18 72 40 64 60" stroke="#ffffff" strokeWidth="1.2" fill="none" opacity=".45" />
      <path d="M48 66L50 72M60 66L58 72" stroke="#5a3a1a" strokeWidth="1" />
      <path d="M49 72H59V80H49Z" fill="#8a5a2a" />
      {/* a smaller one further off */}
      <g transform="translate(96 62) scale(.42)">
        <path d="M0 0C16 0 25 12 25 25C25 40 10 48 6 56H-6C-10 48 -25 40 -25 25C-25 12 -16 0 0 0Z" fill="#3f86e8" />
        <path d="M0 0C9 12 9 40 3 56H-3C-9 40 -9 12 0 0Z" fill="#ffffff" />
        <path d="M-5 62H5V70H-5Z" fill="#8a5a2a" />
      </g>
      <path d="M0 96Q30 84 60 94T120 90V120H0Z" fill="#7cc85a" />
      <path d="M0 108Q40 98 80 108T120 104V120H0Z" fill="#4f9a3a" />
      <path d="M10 100L30 96M40 102L58 98M76 98L96 94" stroke="#a8e08a" strokeWidth="1.2" />
      <path d="M58 104h8v-6l-4-3-4 3Z" fill="#f3e2c0" />
      <path d="M57 98.5l5-4 5 4" stroke="#c8453c" strokeWidth="1.6" fill="none" />
    </>
  );
}

function Rocket({ p }: { p: string }) {
  return (
    <>
      <Sky id={`${p}s`} stops={[[0, '#0d0618'], [0.6, '#3b1066'], [1, '#ff7a59']]} />
      {DOTS.map(([x, y]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r=".9" fill="#ffe6f6" opacity=".8" />
      ))}
      <Sparkle x={96} y={30} r={3.4} />
      <Sparkle x={40} y={40} r={2.4} />
      <g transform="rotate(-20 20 20)">
        <ellipse cx="20" cy="20" rx="16" ry="4" fill="none" stroke="#9fd2ee" strokeWidth="1.6" />
      </g>
      <circle cx="20" cy="20" r="8" fill="#4fb8e8" />
      <path d="M13 17Q20 15 27 17" stroke="#2a7aa8" strokeWidth="1.4" fill="none" />
      <path d="M104 56a8 8 0 1 0 7 11a6 6 0 1 1-7-11Z" fill="#f2ecd2" />
      {/* the rocket, climbing to the right */}
      <g transform="rotate(35 60 62)">
        <path d="M52 86Q60 118 68 86Z" fill="#ffc24a" />
        <path d="M55 86Q60 104 65 86Z" fill="#fff3b0" />
        <path d="M50 70L38 90H50ZM70 70L82 90H70Z" fill="#d64541" />
        <path d="M60 20C68 30 70 44 70 60V86H50V60C50 44 52 30 60 20Z" fill="#e9eef2" />
        <path d="M60 20C64 25 66 30 67 34H53C54 30 56 25 60 20Z" fill="#d64541" />
        <circle cx="60" cy="50" r="6" fill="#4fb8e8" stroke="#8a97a2" strokeWidth="2" />
        <path d="M55 40V80" stroke="#ffffff" strokeWidth="2" opacity=".6" />
        <path d="M50 80H70" stroke="#8a97a2" strokeWidth="1.6" />
      </g>
      {/* the launch smoke */}
      <g fill="#f3e0ee" opacity=".92">
        <circle cx="10" cy="112" r="12" />
        <circle cx="26" cy="104" r="10" />
        <circle cx="40" cy="114" r="10" />
        <circle cx="8" cy="96" r="7" />
      </g>
      <path d="M70 116H120V120H70Z" fill="#1a0b30" />
      <path d="M84 116V104H88V116ZM96 116V98H102V116Z" fill="#1a0b30" />
    </>
  );
}

const DRAW: Record<SceneId, (props: { p: string }) => ReactNode> = {
  ship: Ship,
  planet: Planet,
  city: City,
  tree: Tree,
  lighthouse: Lighthouse,
  mountains: Mountains,
  house: House,
  balloon: Balloon,
  rocket: Rocket,
};

/**
 * One picture, or the part of it inside `view` (x y w h in the 120 box): on a page, or nested in a
 * board's SVG at x, y, width, height (board units).
 */
export const ScenePicture = memo(function ScenePicture({
  scene,
  view = '0 0 120 120',
  x,
  y,
  width = '100%',
  height = '100%',
}: {
  scene: SceneId;
  view?: string;
  x?: number;
  y?: number;
  width?: number | string;
  height?: number | string;
}) {
  const p = `s${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const Draw = DRAW[scene];
  return (
    <svg viewBox={view} x={x} y={y} width={width} height={height} preserveAspectRatio="none" style={{ display: 'block' }} aria-hidden="true">
      <Draw p={p} />
    </svg>
  );
});
